import {
  type GraphNodeList,
  type ScenarioGraph,
  ScenarioGraphNodeType
} from 'types/api/scenarios/scenario'

import type {
  GraphNodePath,
  GraphNodePathMap
} from 'components/scenarios/utils/graph/types'
import type { ScenarioRunGraph } from 'types/api/scenarios/runs/scenario-runs'

// limitation of the backend (dynamo)
export const MAX_GRAPH_NODES = 99

export function isFirstGraphNode(nodeKey: string) {
  return nodeKey === '0' || nodeKey === 'concurrentNode'
}

/**
 *  Recursively handles following graph node children and building up their node path maps
 */
export function generateRecursiveGraphNodePaths(
  nodes: GraphNodeList,
  parentPath: GraphNodePath
) {
  let nodePaths = {}

  Object.entries(nodes ?? {}).forEach(([key, node]) => {
    switch (node.type) {
      case ScenarioGraphNodeType.Concurrent:
        nodePaths[node.guid] = [...parentPath, key]
        node.branches.forEach((branch, branchIndex) => {
          // there's no guid for branches so just add their node tree
          nodePaths = {
            ...nodePaths,
            ...generateRecursiveGraphNodePaths(branch.nodes, [
              ...parentPath,
              key,
              'branches',
              branchIndex,
              'nodes'
            ])
          }
        })
        break

      default:
        nodePaths[node.guid] = [...parentPath, key]
    }
  })

  return nodePaths
}

export function getObserverPath(observerNumber: number): GraphNodePath {
  return ['observers', observerNumber]
}

export function getExternalActionPath(
  externalActionNumber: number
): GraphNodePath {
  return ['external_actions', externalActionNumber]
}

export function getRollbackExternalActionPath(
  rollbackExternalActionNumber: number
): GraphNodePath {
  return ['external_rollbacks', rollbackExternalActionNumber]
}

/**
 * Generates a map of Scenario graph node guids to their path in the graph
 * This is a way for us to be able to quickly walk the graph and grab their node
 */
export function generateGraphNodePathMap(
  graph: ScenarioGraph | ScenarioRunGraph
): GraphNodePathMap {
  const observerPaths = {}
  const externalActionsPaths = {}
  const rollbackExternalActionsPaths = {}

  if (graph?.observers && graph?.observers?.length > 0) {
    graph.observers.forEach(
      (o, n) => (observerPaths[o.guid] = getObserverPath(n))
    )
  }

  if (graph?.external_actions && graph?.external_actions?.length > 0) {
    graph.external_actions.forEach(
      (p, n) => (externalActionsPaths[p.guid] = getExternalActionPath(n))
    )
  }

  if (graph?.external_rollbacks && graph?.external_rollbacks?.length > 0) {
    graph.external_rollbacks.forEach(
      (p, n) =>
        (rollbackExternalActionsPaths[p.guid] =
          getRollbackExternalActionPath(n))
    )
  }

  return {
    ...observerPaths,
    ...externalActionsPaths,
    ...rollbackExternalActionsPaths,
    ...generateRecursiveGraphNodePaths(graph?.nodes, ['nodes'])
  }
}

/**
 * Return the number of nodes in a graph
 *
 * Generates a GraphNodePathMap because that returns only the actual nodes
 */
export function getGraphNodeCount(graph: ScenarioGraph) {
  const graphNodePathMap = generateGraphNodePathMap(graph)
  return Number(Object.keys(graphNodePathMap).length)
}

/**
 * Check if the graph exceeds the total limit for graph nodes
 */
export function doesGraphHaveTooManyNodes(graph: ScenarioGraph) {
  return getGraphNodeCount(graph) > MAX_GRAPH_NODES
}

/**
 * Return the number of nested levels in a graph path
 *
 * We have a limit on the number of nested levels in a graph, so we have to check that we don't exceed that
 * Using the GraphNodePath means we don't have to deal with walking the graph structure
 * We can just count the number of branches in the path
 */
export function getGraphPathNestingLevel(path: GraphNodePath) {
  const numBranchesInPath = path.filter((p) => p === 'branches').length
  // nesting level is the # of branches
  return numBranchesInPath
}
